'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import posthog from 'posthog-js'
import { ArrowRight, Map } from 'lucide-react'
import {
  formatRate,
  type StatsCalculationMethod,
  type VisaCategoryStat,
} from './visaCategoryStats'
import { StatsCalculationPopover } from './StatsCalculationPopover'

interface VisaCategoryResultsProps {
  category: VisaCategoryStat
  calculationMethod: StatsCalculationMethod
}

interface RateBarProps {
  label: string
  rate: number
  variant: 'serotte' | 'national'
}

function RateBar({ label, rate, variant }: RateBarProps) {
  const isSerotte = variant === 'serotte'

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-4">
        <span className={`text-sm md:text-base ${isSerotte ? 'text-white' : 'text-white/55'}`}>
          {label}
        </span>
        <span
          className={`shrink-0 text-base font-semibold tabular-nums md:text-lg ${isSerotte ? 'text-brand-success' : 'text-white/50'
            }`}
        >
          {isSerotte ? formatRate(rate) : `~${formatRate(rate)}`}
        </span>
      </div>
      <div
        className="h-2.5 overflow-hidden rounded-full bg-white/10"
        role="progressbar"
        aria-valuenow={Math.round(rate)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: ${formatRate(rate)}`}
      >
        <motion.div
          className={`h-full rounded-full ${isSerotte ? 'bg-brand-success' : 'bg-white/30'}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(rate, 100)}%` }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        />
      </div>
    </div>
  )
}

function NationalUnavailableNote() {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-4">
        <span className="text-sm md:text-base text-white/55">National average</span>
        <span className="text-sm text-white/35">—</span>
      </div>
      <div
        className="h-2.5 rounded-full border border-dashed border-white/10 bg-white/5"
        aria-hidden
      />
      <p className="mt-2 text-xs leading-snug text-white/40">
        No national average publicly available.
      </p>
    </div>
  )
}

export function VisaCategoryResults({ category, calculationMethod }: VisaCategoryResultsProps) {
  const hasNationalRate = category.nationalApprovalRate != null
  const showNational =
    category.showNationalComparison && hasNationalRate

  const trackLinkClick = (linkType: 'visa' | 'roadmap') => {
    posthog.capture('visa_category_stat_link_clicked', {
      visa_category: category.label,
      link_type: linkType,
      page_url: window.location.pathname,
    })
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={category.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        role="tabpanel"
        aria-live="polite"
        className="grid items-stretch gap-4 pt-6 md:grid-cols-2 md:gap-12 md:pt-8"
      >


        {/* Roadmap card */}
        {category.roadmapUrl && (
          <Link
            href={category.roadmapUrl}
            onClick={() => trackLinkClick('roadmap')}
            className="group relative flex min-h-[10rem] flex-col justify-end overflow-hidden rounded-2xl border border-white/10 bg-brand-blue md:min-h-[12rem]"
          >
            {category.roadmapImageUrl && (
              <Image
                src={category.roadmapImageUrl}
                alt=""
                fill
                sizes="(max-width: 768px) 100vw, 640px"
                className="object-cover opacity-45 transition-all duration-500 group-hover:scale-105 group-hover:opacity-55"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/70 to-primary/20" />

            <div className="relative p-5 md:p-6">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-gold/30 bg-brand-gold/15 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-brand-gold-light">
                <Map className="h-3 w-3" aria-hidden />
                Visa Roadmap
              </span>
              <h3 className="mt-3 text-lg font-medium text-white md:text-xl">
                {category.label} step-by-step roadmap
              </h3>
              <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-gold-light">
                From eligibility to approval
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
              </span>
            </div>
          </Link>
        )}

        {/* Approval rates */}
        <div className="flex flex-col justify-center gap-5 md:gap-6">
          <div className="space-y-4 md:space-y-5">
            <div>
              <RateBar
                label="Our approval rate"
                rate={category.serotteApprovalRate}
                variant="serotte"
              />
              <p className="mt-2 text-xs md:text-sm leading-snug text-white/55">
                {category.label === 'TN' ? (
                  <>
                    {category.casesApproved} approved clients of {category.casesDecided} total
                    applicants under the current Trump administration (since Jan 2025)
                  </>
                ) : (
                  <>
                    {category.casesApproved} approved of {category.casesDecided} adjudicated under
                    the current Trump administration (since Jan 2025)
                  </>
                )}
                <span className="text-white/30 mx-2">·</span>
                {category.casesFiled.toLocaleString()} filed since 2010
              </p>
            </div>
            {showNational ? (
              <RateBar
                label="National average"
                rate={category.nationalApprovalRate!}
                variant="national"
              />
            ) : !hasNationalRate ? (
              <NationalUnavailableNote />
            ) : null}
          </div>

          <div className="flex flex-wrap items-baseline gap-x-1 text-xs md:text-sm text-white/60">
            <StatsCalculationPopover category={category} calculationMethod={calculationMethod} />
            <span className="mx-1 whitespace-nowrap text-white/30" aria-hidden>
              ·
            </span>
            <Link
              href={category.visaPageUrl}
              onClick={() => trackLinkClick('visa')}
              className="whitespace-nowrap underline decoration-white/25 underline-offset-2 hover:text-white transition-colors"
            >
              {category.label} overview
            </Link>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
